import { Box, Typography } from '@mui/material';
import { Marker, type MapRef } from '@vis.gl/react-maplibre';
import MapView from 'components/Map/MapView';
import { useMemo, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMapFlyTo from 'hooks/useMapFlyTo';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { hasNoSatelliteCoverage } from 'utils/siteRoadOverlay';

interface FatigueHotspot {
  lat_mean: string | number;
  lon_mean: string | number;
  value: string | number;
  segment_name?: string;
  pass_count?: number;
  avg_speed?: number;
  avg_payload?: number;
}

interface FatigueMapProps {
  hotspots?: FatigueHotspot[];
  enableDarkTheme: boolean;
  center?: { lat: number; lon: number };
  zoom?: number;
  height?: string | number;
  isMiniMap?: boolean;
  title?: string;
  subtitle?: string;
  hideAdvancedMetrics?: boolean;
}

interface TooltipState {
  x: number;
  y: number;
  hotspot: FatigueHotspot;
}

// Converts the old Cesium camera "altitude" (metres) driving CameraFlyTo into a
// MapLibre zoom level — the two units aren't equivalent, so this is a smooth,
// monotonic approximation tuned so the previous altitude range (200m-8000m)
// lands in a reasonable close-to-far zoom range (roughly 18 down to 12).
const altitudeToZoom = (altitude: number): number => {
  const clamped = Math.min(Math.max(altitude, 100), 20000);
  const zoom = 21 - Math.log2(clamped / 15);
  return Math.min(19, Math.max(9, zoom));
};

// Replaces Cesium's Color.lerp(YELLOW, RED, ratio) with a plain RGB lerp.
const lerpYellowToRed = (
  ratio: number
): { r: number; g: number; b: number } => {
  const clamped = Math.min(Math.max(ratio, 0), 1);
  return {
    r: 255,
    g: Math.round(255 * (1 - clamped)),
    b: 0,
  };
};

const FatigueMap = ({
  hotspots = [],
  enableDarkTheme,
  center: propCenter,
  zoom = 2000,
  height = '100%',
  isMiniMap = false,
  title,
  subtitle,
  hideAdvancedMetrics = false,
}: FatigueMapProps) => {
  const { t } = useTranslation();
  const { customerCode } = useCustomerInfo();
  const mapRef = useRef<MapRef | null>(null);
  const { flyTo } = useMapFlyTo(mapRef);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const validHotspotsRef = useRef<FatigueHotspot[]>([]);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const hideAdvancedMetricsRef = useRef(hideAdvancedMetrics);

  useEffect(() => {
    hideAdvancedMetricsRef.current = hideAdvancedMetrics;
  }, [hideAdvancedMetrics]);

  const validHotspots = useMemo(() => {
    return (hotspots || []).filter(
      (h) =>
        h.lat_mean !== null &&
        h.lat_mean !== undefined &&
        !isNaN(Number(h.lat_mean)) &&
        h.lon_mean !== null &&
        h.lon_mean !== undefined &&
        !isNaN(Number(h.lon_mean))
    );
  }, [hotspots]);
  // Update ref inline — valid for mutable refs, avoids hook ordering issues
  validHotspotsRef.current = validHotspots;

  const center = useMemo(() => {
    if (validHotspots.length > 0) {
      const lat =
        validHotspots.reduce((acc: number, h) => acc + Number(h.lat_mean), 0) /
        validHotspots.length;
      const lon =
        validHotspots.reduce((acc: number, h) => acc + Number(h.lon_mean), 0) /
        validHotspots.length;
      return { lat, lon };
    }
    return propCenter ?? { lat: -34.397, lon: 150.644 };
  }, [validHotspots, propCenter]);

  const maxVal = useMemo(() => {
    if (validHotspots.length === 0) return 1;
    const max = Math.max(...validHotspots.map((h) => Number(h.value) || 0));
    return max > 0 ? max : 1;
  }, [validHotspots]);

  const dynamicZoom = useMemo(() => {
    if (validHotspots.length <= 1) return isMiniMap ? 500 : zoom;
    const lats = validHotspots.map((h) => Number(h.lat_mean));
    const lons = validHotspots.map((h) => Number(h.lon_mean));
    const latDist = Math.abs(Math.max(...lats) - Math.min(...lats)) * 111000;
    const lonDist =
      Math.abs(Math.max(...lons) - Math.min(...lons)) *
      111000 *
      Math.cos((center.lat * Math.PI) / 180);
    const diagonal = Math.sqrt(latDist * latDist + lonDist * lonDist);
    return Math.min(Math.max(diagonal * 1.5, 200), 8000);
  }, [validHotspots, center.lat, isMiniMap, zoom]);

  // react-map-gl only reads initialViewState once on mount — the effect below
  // handles all subsequent camera moves via flyTo, mirroring the old
  // declarative <CameraFlyTo> which re-ran on every center/zoom change.
  const initialViewState = useMemo(
    () => ({
      longitude: center.lon,
      latitude: center.lat,
      zoom: altitudeToZoom(dynamicZoom),
    }),
    []
  );

  useEffect(() => {
    flyTo(center.lat, center.lon, altitudeToZoom(dynamicZoom), 1500);
  }, [center.lat, center.lon, dynamicZoom, flyTo]);

  const handleHotspotClick = (
    e: React.MouseEvent,
    hotspot: FatigueHotspot,
    isHighIntensity: boolean
  ) => {
    e.stopPropagation();
    if (!isHighIntensity || hideAdvancedMetricsRef.current) {
      setTooltip(null);
      return;
    }
    const rect = containerRef.current?.getBoundingClientRect();
    const x = rect ? e.clientX - rect.left : e.clientX;
    const y = rect ? e.clientY - rect.top : e.clientY;
    setTooltip({ x, y, hotspot });
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {/* Dynamic Overlay Header */}
      {(title ?? subtitle) && (
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            left: 20,
            zIndex: 1000,
            padding: '12px 18px',
            backgroundColor: enableDarkTheme
              ? 'rgba(25, 28, 36, 0.85)'
              : 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(4px)',
            borderRadius: '4px',
            border: enableDarkTheme
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(0,0,0,0.1)',
            pointerEvents: 'none',
          }}
        >
          {title && (
            <Typography
              sx={{
                color: enableDarkTheme ? '#fff' : '#000',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                mb: 0.2,
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              sx={{ color: '#8b8e98', fontSize: '9px', fontWeight: 600 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      {/* Custom tooltip overlay */}
      {tooltip && (
        <Box
          onClick={() => {
            setTooltip(null);
          }}
          sx={{
            position: 'absolute',
            left: tooltip.x + 14,
            top: tooltip.y - 30,
            zIndex: 2000,
            backgroundColor: '#111318',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '6px',
            p: '10px 14px',
            minWidth: 180,
            pointerEvents: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
          }}
        >
          {[
            tooltip.hotspot.segment_name &&
              tooltip.hotspot.segment_name !== 'segment_name' && {
                label: t('frame_fatigue_tooltip_id'),
                value: tooltip.hotspot.segment_name,
              },
            {
              label: t('intensity'),
              value: Number(tooltip.hotspot.value || 0).toFixed(1),
            },
            !hideAdvancedMetrics && {
              label: t('no_of_passes'),
              value: tooltip.hotspot.pass_count ?? '—',
            },
            tooltip.hotspot.avg_payload != null && !hideAdvancedMetrics
              ? {
                  label: t('dm_map_payload'),
                  value: `${Number(tooltip.hotspot.avg_payload).toFixed(0)} t`,
                }
              : null,
            tooltip.hotspot.avg_speed != null &&
            tooltip.hotspot.avg_speed !== undefined &&
            !hideAdvancedMetrics
              ? {
                  label: t('speed'),
                  value: `${Number(tooltip.hotspot.avg_speed).toFixed(0)} km/h`,
                }
              : null,
          ]
            .filter(Boolean)
            .map((row: any) => (
              <Box
                key={row.label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 3,
                  mb: 0.5,
                }}
              >
                <Typography sx={{ color: '#aaa', fontSize: '12px' }}>
                  {row.label}
                </Typography>
                <Typography
                  sx={{ color: '#fff', fontSize: '12px', fontWeight: 700 }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
        </Box>
      )}

      <MapView
        ref={mapRef}
        basemap={hasNoSatelliteCoverage(customerCode) ? undefined : 'satellite'}
        style={{ width: '100%', height: '100%' }}
        initialViewState={initialViewState}
        onClick={() => {
          setTooltip(null);
        }}
      >
        {validHotspots.map((h, i) => {
          const ratio = maxVal > 0 ? (Number(h.value) || 0) / maxVal : 0;
          const isHighIntensity = ratio > 0.8;
          const { r, g, b } = lerpYellowToRed(ratio);
          const dotSize = isHighIntensity ? 14 : 10;
          const haloSize = 26;

          return (
            <Marker
              key={`hotspot-${i}`}
              longitude={Number(h.lon_mean)}
              latitude={Number(h.lat_mean)}
            >
              <Box
                onClick={(e) => {
                  handleHotspotClick(e, h, isHighIntensity);
                }}
                sx={{
                  position: 'relative',
                  width: haloSize,
                  height: haloSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                {isHighIntensity && (
                  <Box
                    sx={{
                      position: 'absolute',
                      width: haloSize,
                      height: haloSize,
                      borderRadius: '50%',
                      backgroundColor: `rgba(${r}, ${g}, ${b}, 0.25)`,
                      border: `2px solid rgba(${r}, ${g}, ${b}, 0.6)`,
                    }}
                  />
                )}
                <Box
                  sx={{
                    position: 'relative',
                    width: dotSize,
                    height: dotSize,
                    borderRadius: '50%',
                    backgroundColor: `rgb(${r}, ${g}, ${b})`,
                    border: '1px solid #ffffff',
                  }}
                />
              </Box>
            </Marker>
          );
        })}
      </MapView>
    </Box>
  );
};

export default FatigueMap;
